import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  ChatBubbleLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeSlashIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';
import RatingStars from './RatingStars';
import api from '../utils/api';

const ReviewManagementPanel = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, published, hidden
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    published: 0,
    hidden: 0
  });

  // Fetch reviews
  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/reviews', {
        params: {
          status: filter !== 'all' ? filter : undefined,
          search: searchTerm || undefined
        }
      });

      if (response.success) {
        console.log('📝 Admin Reviews Response:', response.data);
        if (response.data.reviews && response.data.reviews.length > 0) {
          console.log('📸 İlk yorumun fotoğrafları:', response.data.reviews[0].product_review_images);
          console.log('📦 İlk yorumun ürünü:', response.data.reviews[0].products);
        }
        setReviews(response.data.reviews || []);
        setStats(response.data.stats || stats);
      }
    } catch (error) {
      console.error('Yorumlar yüklenirken hata:', error);
      toast.error('Yorumlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [filter, searchTerm]);

  // Approve review
  const handleApprove = async (reviewId) => {
    try {
      const response = await api.patch(`/api/admin/reviews/${reviewId}/status`, {
        status: 'published'
      });

      if (response.success) {
        toast.success('Yorum onaylandı');
        fetchReviews();
      }
    } catch (error) {
      toast.error('Yorum onaylanamadı');
    }
  };

  // Reject/Hide review
  const handleHide = async (reviewId) => {
    try {
      const response = await api.patch(`/api/admin/reviews/${reviewId}/status`, {
        status: 'hidden'
      });

      if (response.success) {
        toast.success('Yorum gizlendi');
        fetchReviews();
      }
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  // Delete review
  const handleDelete = async (reviewId) => {
    if (!window.confirm('Bu yorumu kalıcı olarak silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await api.delete(`/api/admin/reviews/${reviewId}`);

      if (response.success) {
        toast.success('Yorum silindi');
        fetchReviews();
      }
    } catch (error) {
      toast.error('Yorum silinemedi');
    }
  };

  // Filter badges
  const FilterBadge = ({ status, count, label }) => (
    <button
      onClick={() => setFilter(status)}
      className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
        filter === status
          ? 'bg-brand-green text-white shadow-lg'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {label} <span className="ml-1">({count})</span>
    </button>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center">
            <ChatBubbleLeftIcon className="w-6 h-6 text-brand-green" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Yorum Yönetimi</h2>
            <p className="text-gray-600 text-sm">Müşteri yorumlarını yönetin</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
          <div className="text-2xl font-bold text-blue-900">{stats.total}</div>
          <div className="text-sm text-blue-700">Toplam Yorum</div>
        </div>
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-xl">
          <div className="text-2xl font-bold text-yellow-900">{stats.pending}</div>
          <div className="text-sm text-yellow-700">Bekleyenler</div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
          <div className="text-2xl font-bold text-green-900">{stats.published}</div>
          <div className="text-sm text-green-700">Yayında</div>
        </div>
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-xl">
          <div className="text-2xl font-bold text-gray-900">{stats.hidden}</div>
          <div className="text-sm text-gray-700">Gizlenenler</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <FunnelIcon className="w-5 h-5 text-gray-600" />
            <FilterBadge status="all" count={stats.total} label="Tümü" />
            <FilterBadge status="pending" count={stats.pending} label="Bekleyen" />
            <FilterBadge status="published" count={stats.published} label="Yayında" />
            <FilterBadge status="hidden" count={stats.hidden} label="Gizli" />
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Yorum veya ürün ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green"
            />
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-brand-green border-t-transparent" />
          </div>
        ) : reviews.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {reviews.map((review) => (
              <div key={review.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  {/* Review Content */}
                  <div className="flex-1">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">
                          {review.author_name || 'Anonim'}
                        </span>
                        {review.is_verified && (
                          <CheckBadgeIcon className="w-5 h-5 text-green-600" title="Doğrulanmış Alıcı" />
                        )}
                      </div>
                      <span className="text-sm text-gray-500">
                        {new Date(review.created_at).toLocaleDateString('tr-TR')}
                      </span>
                      {/* Status Badge */}
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          review.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : review.status === 'published'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {review.status === 'pending' && '⏳ Bekliyor'}
                        {review.status === 'published' && '✅ Yayında'}
                        {review.status === 'hidden' && '🚫 Gizli'}
                      </span>
                    </div>

                    {/* Rating */}
                    <RatingStars rating={review.rating} size="sm" readonly className="mb-2" />

                    {/* Review Text */}
                    <p className="text-gray-700 mb-3">{review.review_body}</p>

                    {/* Product Info */}
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Ürün:</span> {review.products?.name || 'Bilinmiyor'}
                    </div>

                    {/* Images */}
                    {review.product_review_images && review.product_review_images.length > 0 && (
                      <div className="flex gap-2 mt-3">
                        {review.product_review_images
                          .sort((a, b) => a.display_order - b.display_order)
                          .map((img) => (
                            <img
                              key={img.id}
                              src={img.image_url}
                              alt="Yorum foto\u011fraf\u0131"
                              className="w-20 h-20 object-cover rounded-lg border border-gray-200 cursor-pointer hover:scale-105 transition-transform"
                              onClick={() => window.open(img.image_url, '_blank')}
                            />
                          ))}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    {review.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(review.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                        >
                          <CheckCircleIcon className="w-4 h-4" />
                          Onayla
                        </button>
                        <button
                          onClick={() => handleHide(review.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                        >
                          <EyeSlashIcon className="w-4 h-4" />
                          Gizle
                        </button>
                      </>
                    )}

                    {review.status === 'published' && (
                      <button
                        onClick={() => handleHide(review.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                      >
                        <EyeSlashIcon className="w-4 h-4" />
                        Gizle
                      </button>
                    )}

                    {review.status === 'hidden' && (
                      <button
                        onClick={() => handleApprove(review.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        <CheckCircleIcon className="w-4 h-4" />
                        Yayınla
                      </button>
                    )}

                    <button
                      onClick={() => handleDelete(review.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                    >
                      <XCircleIcon className="w-4 h-4" />
                      Sil
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <ChatBubbleLeftIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Yorum bulunamadı</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewManagementPanel;
